// src/components/common/Toast.jsx
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

const TONE_CLASS = {
  success: 'bg-emerald-600',
  error: 'bg-red-600',
  info: 'bg-gray-900 dark:bg-gray-100 dark:text-gray-900',
};
const TONE_DURATION = { success: 2600, info: 2600, error: 3600 };

const ToastContext = createContext(null);

// 模块级通道：不方便使用 hook 的页面可以直接 import { showToast }。
let activeSink = null;

export function showToast(message, tone = 'success') {
  activeSink?.(message, tone);
}

/**
 * 全局轻提示（右下角悬浮，与超级后台一致）。
 * 层级高于 BottomSheet(z-90) 与 Modal(z-80)，弹层内保存也能看到反馈。
 */
export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const idRef = useRef(0);
  const timersRef = useRef(new Map());

  const dismissToast = useCallback((id) => {
    const timer = timersRef.current.get(id);
    if (timer) { clearTimeout(timer); timersRef.current.delete(id); }
    setToasts((current) => current.filter((item) => item.id !== id));
  }, []);

  const pushToast = useCallback((message, tone = 'success') => {
    const text = String(message ?? '').trim();
    if (!text) return;
    idRef.current += 1;
    const id = idRef.current;
    // 只保留最近三条，避免连续操作堆满屏幕。
    setToasts((current) => [...current.slice(-2), { id, text, tone:TONE_CLASS[tone] ? tone : 'success' }]);
    timersRef.current.set(id, setTimeout(() => {
      timersRef.current.delete(id);
      setToasts((current) => current.filter((item) => item.id !== id));
    }, TONE_DURATION[tone] || TONE_DURATION.success));
  }, []);

  useEffect(() => {
    activeSink = pushToast;
    return () => { if (activeSink === pushToast) activeSink = null; };
  }, [pushToast]);

  useEffect(() => () => {
    timersRef.current.forEach((timer) => clearTimeout(timer));
    timersRef.current.clear();
  }, []);

  const value = useMemo(() => ({ showToast:pushToast, dismissToast }), [pushToast, dismissToast]);

  return <ToastContext.Provider value={value}>
    {children}
    <div role="status" aria-live="polite" className="pointer-events-none fixed inset-x-0 bottom-0 z-[130] flex flex-col items-end gap-2 px-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">
      {toasts.map((toast) => <button key={toast.id} type="button" onClick={() => dismissToast(toast.id)}
        className={`pointer-events-auto max-w-[85%] break-words rounded-xl px-4 py-2.5 text-left text-sm text-white shadow-lg ${TONE_CLASS[toast.tone]}`}>
        {toast.text}
      </button>)}
    </div>
  </ToastContext.Provider>;
}

const FALLBACK = { showToast:() => {}, dismissToast:() => {} };

export function useToast() {
  return useContext(ToastContext) || FALLBACK;
}

export default ToastProvider;
